import { Checkbox, FormControlLabel, styled } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Flip, ToastContainer, toast } from "react-toastify";
import api from "../../../features/auth/axiosConfig";
import { ModalWrapper } from "../../../shared/components/ModalWrapper";
import CommonButtons from "../../../shared/components/CommonButtons";
import { User } from "src/shared/types";

interface Branchlocation {
  _id: { $oid: string };
  name: string;
  addresses: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    country: string
    state: string;
    postalCode: string
  };
}

interface Props {
  id: string;
  onClose: () => void;
}

const DropdownContainer = styled("div")({
  position: "relative",
  width: "100%",
  minWidth: "180px",
});

const DropdownHeader = styled("div")<{ isOpen: boolean }>(({ isOpen }) => ({
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  backgroundColor: "#fafafa",
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: "15px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  transition: "all 0.2s ease",
  borderColor: isOpen ? "#2563eb" : "#d1d5db",
  "&:hover": {
    borderColor: "#2563eb",
    backgroundColor: "#f3f4f6",
  },
}));

const DropdownList = styled("div")({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  backgroundColor: "white",
  maxHeight: "200px",
  overflowY: "auto",
  zIndex: 50,
  boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
  marginTop: "4px",
});

const BranchOption = styled("div")<{ selected?: boolean }>(({ selected }) => ({
  padding: "8px 12px",
  cursor: "pointer",
  backgroundColor: selected ? "#dbeafe" : "transparent",
  "&:hover": {
    backgroundColor: selected ? "#dbeafe" : "#f3f4f6",
  },
  borderBottom: "1px solid #f0f0f0",
  "&:last-child": {
    borderBottom: "none",
  },
}));

const DropdownIcon = styled("span")<{ isOpen: boolean }>(({ isOpen }) => ({
  width: "16px",
  height: "16px",
  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
  transition: "transform 0.2s ease-in-out",
  "&::after": {
    content: '""',
    width: "0",
    height: "0",
    borderLeft: "4px solid transparent",
    borderRight: "4px solid transparent",
    borderTop: "5px solid #6b7280",
  },
}));

const RoleSelect: React.FC<Props> = ({ id, onClose }) => {
  const [user, setUser] = useState<User | null>(null);
  const [branches, setBranches] = useState<Branchlocation[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branchlocation | null>(null);
  const [admin, setAdmin] = useState(false);
  const [employee, setEmployee] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branchId, setBranchId] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    api.get(`/pos/v2/get_user`).then((res) => {
      const users: User[] = res.data.result;
      setUser(users.find((u) => u._id.$oid === id) || null);
    });

    api.get(`/app/pos/v2/branch`).then((res) => {
      setBranches(res.data.result || []);
      setSelectedBranch(null);
      setBranchId("");
    });
  }, [id]);

  

  const handleSubmit = async () => {
    if (!selectedBranch) {
      toast.error("Please select a branch before submitting.", { transition: Flip });
      return;
    }
    try {
      setLoading(true);
      const role = admin ? "Admin" : "Employee";
      const payload = { employee: [selectedBranch], role };

      await api.put(`/pos/v2/add/${id}`, payload);

      if (admin) {
        const branchUpdate = {
          contactPerson: [user?.firstName, user?.middleName, user?.lastName].filter(Boolean).join(" "),
          contactNumber: user?.phones[0].phone,
        };
        await api.put(`/app/pos/v2/branch/${branchId}`, branchUpdate);
      }

      toast.success("Branch assigned successfully!", { transition: Flip });
      setTimeout(onClose, 1500);
      window.location.reload();
    } catch {
      toast.error("Failed to assign branch.", { transition: Flip });
    } finally {
      setLoading(false);
    }
  };

  const handleBranchSelect = (branch: Branchlocation) => {
    setSelectedBranch(branch);
    setBranchId(branch._id.$oid);
    setIsDropdownOpen(false);
  };

  return (
    <ModalWrapper title="Assign Role & Branch" onClose={onClose} onBack={onClose} showCloseButton showBackButton width="max-w-2xl" className="bg-white shadow-lg rounded-lg">
      <div className="p-6 space-y-6 text-gray-800 text-base">
        {/* Role Selection */}
        <div className="flex flex-col gap-3">
          <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
            Role <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-6 bg-gray-50 p-3 rounded-md border border-gray-200">
            <FormControlLabel
              control={
                <Checkbox
                  checked={admin}
                  onChange={() => {
                    setAdmin(!admin);
                    setEmployee(false);
                  }}
                  sx={{
                    color: "#2563eb",
                    "&.Mui-checked": { color: "#2563eb" },
                    "& .MuiSvgIcon-root": { fontSize: 22 },
                  }}
                />
              }
              label={<span className="text-sm font-medium">Admin</span>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={employee}
                  onChange={() => {
                    setEmployee(!employee);
                    setAdmin(false);
                  }}
                  sx={{
                    color: "#2563eb",
                    "&.Mui-checked": { color: "#2563eb" },
                    "& .MuiSvgIcon-root": { fontSize: 22 },
                  }}
                />
              }
              label={<span className="text-sm font-medium">Employee</span>}
            />
          </div>
        </div>

        {/* Branch Selection */}
        <div className="flex flex-col gap-3">
          <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
            Select Branch <span className="text-red-500">*</span>
          </label>
          <DropdownContainer>
            <DropdownHeader isOpen={isDropdownOpen} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
              {selectedBranch ? selectedBranch.name : <span className="text-gray-400">Select a branch</span>}
              <DropdownIcon isOpen={isDropdownOpen} />
            </DropdownHeader>
            {isDropdownOpen && (
              <DropdownList>
                {branches.length === 0 ? (
                  <BranchOption>No branches available</BranchOption>
                ) : (
                  branches.map((branch) => (
                    <BranchOption key={branch._id.$oid} selected={selectedBranch?._id.$oid === branch._id.$oid} onClick={() => handleBranchSelect(branch)}>
                      {branch.name}
                    </BranchOption>
                  ))
                )}
              </DropdownList>
            )}
          </DropdownContainer>
        </div>

        {/* Branch Details */}
        {selectedBranch && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-3">
            <h4 className="font-semibold text-gray-700">Branch Details</h4>
            <div className="text-sm text-gray-700 space-y-1">
              <p>
                <span className="font-medium">Branch Name:</span> {selectedBranch.name}
              </p>
              <p>
                <span className="font-medium">addressLine1:</span> {selectedBranch.addresses.addressLine1}
              </p>
              <p>
                <span className="font-medium">State:</span> {selectedBranch.addresses.state}
              </p>
              <p>
                <span className="font-medium">Pincode:</span> {selectedBranch.addresses.postalCode}
              </p>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <CommonButtons label="Cancel" variant="outlineDanger" onClick={onClose} className="min-w-[100px] h-10 text-sm" disabled={loading} />
          <CommonButtons
            label={loading ? "Assigning..." : "Confirm Assignment"}
            variant="primary"
            onClick={handleSubmit}
            className="min-w-[140px] h-10 text-sm"
            disabled={loading || (!admin && !employee) || !selectedBranch}
          />
        </div>
      </div>
      <ToastContainer />
    </ModalWrapper>
  );
};

export default RoleSelect;
